import React, {useCallback, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import {BulkActionProgress} from 'src/components/bulk-tool';
import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {STACKOVERFLOW_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {
    getUserCrmUpdateProgress,
    stopUserCrmBulkUpdate,
    updateUsersCrmIds,
} from 'src/openapi/admin/apis/BulkCrmUpdateApi';
import {CrmIdType as CrmIdTypes} from 'src/openapi/admin/models/CrmIdType';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';

import {BulkUpdateCrmIdsModalFooter} from './components/bulk-update-crm-ids-modal-footer';
import {PreviewFileContentBlock} from './components/views/preview-file-content-block';
import {SelectCrmType} from './components/views/select-crm-type-block';
import {UploadFileBlock} from './components/views/upload-file-block';
import {TOOL_PROGRESS_LABEL, TOOL_TITLE} from './constants';
import {
    selectUpdateCrmIdsTask,
    selectUpdateCrmIdsUuid,
    setBackgroundTask,
    setShown,
    setUuid,
} from './update-crm-ids.slice';

import type {UserCRMIDType} from './typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {BulkUserCrmId} from 'src/openapi/admin/models/BulkUserCrmId';
import type {CrmIdType} from 'src/openapi/admin/models/CrmIdType';
import type {UserCrmIds} from 'src/openapi/admin/models/UserCrmIds';


type Props = {
    onClose: () => void,
    onSubmit: () => void,
    onPendingChange: (pending: boolean) => void,
};

export const BulkUpdateCRMIDSModal = ({onClose, onSubmit, onPendingChange}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const [currentStep, setCurrentStep] = useState(0);
    const [addedFile, setAddedFile] = useState<File | null>(null);
    const [selectedUser, setSelectedUser] = useState<Array<string>>([]);
    const [uploadFileError, setUploadFileError] = useState<string>('');

    const [selectedCRMType, setSelectedCRMType] = useState<CrmIdType>(CrmIdTypes.SFDC);
    const [usersCRMIDs, setUserCRMIDs] = useState<Array<UserCrmIds> | null>(null);

    const [templateData, setTemplateData] = useState<Array<UserCRMIDType>>([]);
    const createBackgroundTask = useBackgroundTaskManager();

    const submit = useCallback(async (options?: ActionOptions) => {
        if (usersCRMIDs) {
            const crmType = selectedCRMType;
            if (!crmType) {
                throw new Error('CRM Type is undefined');
            }
            const bulkUserCrmIds: BulkUserCrmId[] = selectedUser.map(email => {
                const crmId = templateData.find(q => q.userEmail === email)?.crmId || '';
                const userId = usersCRMIDs.find(q => q.userEmail === email)?.userId || 0;
                return {userId, crmId, crmType};
            });

            const runUpdateResponse = await apiDispatch(updateUsersCrmIds({
                ...options,
                bulkUserCrmId: bulkUserCrmIds,
            }));

            if (runUpdateResponse.payload.success) {
                onSubmit();
                const uuid = runUpdateResponse.payload.data;
                const backgroundTask = createBackgroundTask(
                    TOOL_TITLE,
                    <BulkActionProgress
                        label={TOOL_PROGRESS_LABEL}
                        taskSelector={selectUpdateCrmIdsTask}
                        idSelector={selectUpdateCrmIdsUuid}
                        getProgress={id => apiDispatch(getUserCrmUpdateProgress({id}))}
                        stopAction={id => apiDispatch(stopUserCrmBulkUpdate({id}))}
                        setShown={setShown}
                        onDone={() => undefined}
                    />,
                    {
                        maxStack: 1,
                        stackOverflowMessage: STACKOVERFLOW_MESSAGE,
                    },
                );

                if (backgroundTask) {
                    dispatch(setUuid(uuid));
                    dispatch(setBackgroundTask(backgroundTask));
                }
            } else {
                return getError({error: runUpdateResponse.payload.data, separator: ' '});
            }
        }
    }, [
        usersCRMIDs,
        selectedCRMType,
        selectedUser,
        apiDispatch,
        createBackgroundTask,
        dispatch,
        onSubmit,
        templateData,
    ]);

    const onLoadFile = useCallback((file: File | null) => {
        setTemplateData([]);
        setUserCRMIDs([]);
        setAddedFile(file);
        setUserCRMIDs(null);
    }, []);

    const steps = useMemo(() => [
        {
            caption: 'Upload a users list for CRM IDs update',
            content: (
                <UploadFileBlock
                    {...{addedFile, onLoadFile, uploadFileError, setUploadFileError}}
                />
            ),
            footer: (
                <BulkUpdateCrmIdsModalFooter
                    toPrevBlock={() => undefined}
                    toNextBlock={() => setCurrentStep(1)}
                    prevDisabled={true}
                    nextDisabled={!addedFile || !!uploadFileError}
                />
            ),
        },
        {
            caption: 'Select CRM Type',
            content: (
                <SelectCrmType
                    {...{
                        selectedCRMType,
                        setSelectedCRMType,
                    }}
                />
            ),
            footer: (
                <BulkUpdateCrmIdsModalFooter
                    toPrevBlock={() => {
                        setAddedFile(null);
                        setCurrentStep(0);
                    }}
                    toNextBlock={() => setCurrentStep(2)}
                />
            ),
        },
        {
            caption: 'Review a users list for CRM IDs update',
            content: (
                <PreviewFileContentBlock
                    {...{
                        selectedUser,
                        setSelectedUser,
                        templateData,
                        setTemplateData,
                        usersCRMIDs,
                        setUserCRMIDs,
                        selectedCRMType,
                        addedFile,
                    }}
                />
            ),
            footer: (
                <BulkActionFooter onPendingChange={onPendingChange} action="Update" onConfirm={submit} disabled={!usersCRMIDs || !selectedUser.length}>
                    <Button variant="Type.SECONDARY" onClick={() => setCurrentStep(1)}>Cancel</Button>
                </BulkActionFooter>
            ),
        },
    ], [
        addedFile,
        onPendingChange,
        onLoadFile,
        selectedCRMType,
        selectedUser,
        submit,
        templateData,
        uploadFileError,
        usersCRMIDs,
    ]);

    const {caption, content, footer} = steps[currentStep];

    return (
        <>
            <DialogHeader onClose={onClose}>
                {caption}
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                {content}
            </DialogMain>
            {currentStep === 2 ? footer : <DialogFooter>{footer}</DialogFooter>}
        </>
    );
};
